import React from 'react';
import { Percent, Trophy, Clock, Settings } from 'lucide-react';

// Winner criteria options
export const WINNER_CRITERIA = [
    { value: 'CTR', label: 'Click-Through Rate', description: 'Highest percentage of clicks vs impressions' },
    { value: 'CPA', label: 'Cost Per Acquisition', description: 'Lowest cost per conversion' },
    { value: 'ROAS', label: 'Return on Ad Spend', description: 'Highest revenue per dollar spent' },
    { value: 'conversions', label: 'Total Conversions', description: 'Highest number of conversions' },
    { value: 'engagement', label: 'Engagement Rate', description: 'Highest likes, comments, shares' }
];

// Duration presets
export const DURATION_PRESETS = [
    { days: 7, label: '1 Week' },
    { days: 14, label: '2 Weeks' },
    { days: 21, label: '3 Weeks' },
    { days: 30, label: '1 Month' }
];

/**
 * ABTestForm - Reusable form component for A/B Test node editing
 * Used in both ABTestModal and PropertiesPanel
 */
const ABTestForm = ({
    formData,
    onChange,
    errors = {},
    variantCount = 2
}) => {
    const handleChange = (field, value) => {
        onChange(field, value);
    };

    const handleSplitChange = (index, value) => {
        const newValue = Math.max(0, Math.min(100, parseInt(value) || 0));
        const trafficSplit = formData.trafficSplit || [50, 50];
        const newSplit = [...trafficSplit];
        newSplit[index] = newValue;

        // Adjust other splits proportionally
        const total = newSplit.reduce((a, b) => a + b, 0);
        if (total !== 100) {
            const diff = 100 - total;
            const otherIndex = index === 0 ? 1 : 0;
            newSplit[otherIndex] = Math.max(0, Math.min(100, newSplit[otherIndex] + diff));
        }

        onChange('trafficSplit', newSplit);
    };

    // Generate variant labels
    const getVariantLabel = (index) => String.fromCharCode(65 + index);

    const trafficSplit = formData.trafficSplit || [50, 50];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Test Name */}
            <div className="input-group">
                <label className="input-label">Test Name *</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., Headline Test - CTA Comparison"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    style={errors.name ? { borderColor: 'var(--color-error)' } : {}}
                />
                {errors.name && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.name}
                    </span>
                )}
            </div>

            {/* Traffic Split */}
            <div className="input-group">
                <label className="input-label">
                    <Percent size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Traffic Split
                </label>

                {/* Split visualization */}
                <div style={{
                    display: 'flex',
                    borderRadius: 'var(--radius-full)',
                    overflow: 'hidden',
                    height: '32px',
                    marginBottom: 'var(--space-3)'
                }}>
                    {trafficSplit.map((percent, index) => {
                        const colors = [
                            'var(--color-primary)',
                            'var(--color-info)',
                            'var(--color-success)',
                            'var(--color-warning)'
                        ];
                        return (
                            <div
                                key={index}
                                style={{
                                    width: `${percent}%`,
                                    background: colors[index % colors.length],
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    transition: 'width var(--transition-normal)'
                                }}
                            >
                                {percent > 10 && `${getVariantLabel(index)}: ${percent}%`}
                            </div>
                        );
                    })}
                </div>

                {/* Split inputs */}
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    {trafficSplit.map((percent, index) => (
                        <div key={index} style={{ flex: 1 }}>
                            <label style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                marginBottom: 'var(--space-1)',
                                display: 'block'
                            }}>
                                Variant {getVariantLabel(index)}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="number"
                                    className="input-field"
                                    value={percent}
                                    onChange={(e) => handleSplitChange(index, e.target.value)}
                                    min="0"
                                    max="100"
                                    style={{ paddingRight: 'var(--space-6)' }}
                                />
                                <span style={{
                                    position: 'absolute',
                                    right: 'var(--space-3)',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    %
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
                {errors.split && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.split}
                    </span>
                )}
            </div>

            {/* Winner Criteria */}
            <div className="input-group">
                <label className="input-label">
                    <Trophy size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Winner Criteria
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {WINNER_CRITERIA.map(criteria => (
                        <label
                            key={criteria.value}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: formData.winnerCriteria === criteria.value
                                    ? 'var(--color-warning-light)'
                                    : 'var(--color-bg-secondary)',
                                border: formData.winnerCriteria === criteria.value
                                    ? '2px solid var(--color-warning)'
                                    : '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer'
                            }}
                        >
                            <input
                                type="radio"
                                name="winnerCriteria"
                                value={criteria.value}
                                checked={formData.winnerCriteria === criteria.value}
                                onChange={(e) => handleChange('winnerCriteria', e.target.value)}
                                style={{ marginTop: '2px' }}
                            />
                            <div>
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-text-primary)'
                                }}>
                                    {criteria.label}
                                </div>
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    {criteria.description}
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
            </div>

            {/* Duration */}
            <div className="input-group">
                <label className="input-label">
                    <Clock size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Test Duration
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {DURATION_PRESETS.map(preset => (
                        <button
                            key={preset.days}
                            type="button"
                            onClick={() => handleChange('duration', preset.days)}
                            style={{
                                flex: 1,
                                padding: 'var(--space-2)',
                                background: formData.duration === preset.days
                                    ? 'var(--color-primary)'
                                    : 'var(--color-bg-secondary)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)',
                                color: formData.duration === preset.days
                                    ? 'white'
                                    : 'var(--color-text-primary)'
                            }}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
                <div style={{ marginTop: 'var(--space-2)' }}>
                    <label style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        Custom duration (days):
                    </label>
                    <input
                        type="number"
                        className="input-field"
                        value={formData.duration || 14}
                        onChange={(e) => handleChange('duration', parseInt(e.target.value) || 7)}
                        min="1"
                        max="90"
                        style={{ width: '100px', marginLeft: 'var(--space-2)' }}
                    />
                </div>
            </div>

            {/* Advanced Settings */}
            <div className="input-group">
                <label className="input-label">
                    <Settings size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Advanced Settings
                </label>

                {/* Auto-pause loser */}
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    marginBottom: 'var(--space-2)'
                }}>
                    <input
                        type="checkbox"
                        checked={formData.autoPauseLoser ?? true}
                        onChange={(e) => handleChange('autoPauseLoser', e.target.checked)}
                    />
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-primary)'
                        }}>
                            Auto-pause losing variant
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            Automatically pause underperforming variants when a winner is determined
                        </div>
                    </div>
                </label>

                {/* Statistical settings */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                    <div>
                        <label style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-1)',
                            display: 'block'
                        }}>
                            Min. Sample Size
                        </label>
                        <input
                            type="number"
                            className="input-field"
                            value={formData.minimumSampleSize || 1000}
                            onChange={(e) => handleChange('minimumSampleSize', parseInt(e.target.value) || 100)}
                            min="100"
                        />
                    </div>
                    <div>
                        <label style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-1)',
                            display: 'block'
                        }}>
                            Confidence Level
                        </label>
                        <select
                            className="select-field"
                            value={formData.confidenceLevel || 95}
                            onChange={(e) => handleChange('confidenceLevel', parseInt(e.target.value))}
                        >
                            <option value={90}>90%</option>
                            <option value={95}>95%</option>
                            <option value={99}>99%</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ABTestForm;
